/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ApiKeyFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Application Keys module.
----------------------------------------------------------------*/


import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Key, Search, Copy, Plus } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { navigateToApiKeyDuplicate } from './apiKeysActions';
import { useApiKeyForm, useApiKeyData, useQuickList, useApiKeySubmit } from './hooks';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ApiKeyInfoBox } from './components/ApiKeyInfoBox';
import { ApiKeyGeneratedBanner } from './components/ApiKeyGeneratedBanner';
import { ApiKeyInfoPanel } from './components/ApiKeyInfoPanel';
import { ApiKeyQuickListModal } from './components/ApiKeyQuickListModal';
import type { ApiKey } from './types';

export default function ApiKeyFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { setCurrentPageTitle } = useNavigationStore();
    const { showInfo } = useNotifications();

    const duplicateId = searchParams.get('duplicate');
    const mode = id === 'new' || duplicateId ? 'create' : 'edit';

    useFixedActionBar(true);

    const formRef = useRef<HTMLFormElement>(null);
    const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);

    // -- Hooks -------------------------------------------------
    const { formData, originalData, errors: formErrors, setFormData, setOriginalData, handleInputChange, hasChanges } = useApiKeyForm();

    const { tenants, loadingTenants, domains, loadingDomains, loadDomains } = useApiKeyData(
        mode, id, duplicateId, setFormData, setOriginalData, setGeneratedApiKey,
    );

    const { showQuickList, setShowQuickList, quickListSearch, setQuickListSearch, filteredQuickList, isQuickListLoading } = useQuickList();

    const { saving, isDeleting, showDeleteModal, errors: submitErrors, handleSubmit, handleCancel, handleDeleteClick, handleDeleteCancel, handleDelete } = useApiKeySubmit(mode, id, formData, originalData, hasChanges, setOriginalData, setGeneratedApiKey);

    const errors = { ...formErrors, ...submitErrors };

    useEffect(() => {
        setCurrentPageTitle(null);
        return () => setCurrentPageTitle(null);
    }, [setCurrentPageTitle]);

    // -- Render ------------------------------------------------
    return (
        <div className="p-6">
            <ModuleFormHeader
                icon={<Key className="h-5 w-5 text-primary" />}
                title={mode === 'create' ? t('apiKeys.createApiKey') : `${t('apiKeys.editApiKey')} - ${formData.name || ''}`}
                backButtonText={t('common.list')}
                onBack={() => navigate('/admin/admin/app-keys')}
                actions={[
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('apiKeys.quickList'), onClick: () => setShowQuickList(true) },
                    ...(mode === 'edit' && id && id !== 'new' && !duplicateId
                        ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('common.duplicate'), onClick: () => navigateToApiKeyDuplicate(navigate, id) }]
                        : []),
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('apiKeys.addApiKey'), onClick: () => navigate('/admin/admin/app-keys/new') },
                ]}
            />

            <div className="mt-8">
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    <ApiKeyInfoBox
                        formData={formData} errors={errors}
                        tenants={tenants} loadingTenants={loadingTenants}
                        domains={domains} loadingDomains={loadingDomains}
                        onInputChange={handleInputChange} onLoadDomains={loadDomains}
                    />
                    {generatedApiKey && <ApiKeyGeneratedBanner apiKey={generatedApiKey} />}
                    <ApiKeyInfoPanel />
                </form>
            </div>

            <ApiKeyQuickListModal
                isOpen={showQuickList} currentItemId={mode === 'edit' && !duplicateId ? id : null}
                items={filteredQuickList} isLoading={isQuickListLoading}
                searchValue={quickListSearch} onSearchChange={setQuickListSearch}
                onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
            />

            <FixedActionBar
                onSave={async () => { await handleSubmit(); }}
                onCancel={handleCancel}
                onDelete={mode === 'edit' ? handleDeleteClick : undefined}
                isEditMode={mode === 'edit'}
                saveText={saving ? t('common.loading') : t('common.save')}
                cancelText={t('common.cancel')}
                deleteText={t('common.delete')}
                saveDisabled={saving}
                isLoading={saving}
                hasChanges={hasChanges}
            />

            <ConfirmationModal
                isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDelete}
                title={t('apiKeys.deleteConfirmTitle')} message={t('apiKeys.deleteConfirmMessage')}
                confirmText={t('apiKeys.deleteConfirm')} cancelText={t('common.cancel')}
                type="danger" isLoading={isDeleting}
            />
        </div>
    );
}
